/**
 * UI Sound Effects
 *
 * The soft, dulled "tick" the main page plays when you hover a menu card or
 * flick through the zodiac carousel. It lived inside MainPage, which meant the
 * rest of the site had no tactile feedback at all — so it moved here and every
 * page can use the same sound for its own controls (PLAY NOW, the back arrows).
 *
 * One AudioContext and one decoded buffer for the whole app, created on first
 * use. The recipe (low-pass + very low gain) is the original MainPage one, kept
 * exactly so the sound does not change character now that it is shared.
 */

const SOUND_URL = '/on-001-337979.mp3';
const LOWPASS_HZ = 400;   // cut the highs so the click reads as a dull tick
const GAIN = 0.02;        // deliberately quiet — this is feedback, not an event

let audioContext: AudioContext | null = null;
let audioBuffer: AudioBuffer | null = null;
let loadPromise: Promise<void> | null = null;

/**
 * Fetch and decode the sound once. Failures are swallowed: audio is decoration
 * here, and a blocked fetch or an unsupported browser must never break a page.
 */
function ensureLoaded(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const context = new Ctor();
      const response = await fetch(SOUND_URL);
      const arrayBuffer = await response.arrayBuffer();
      audioBuffer = await context.decodeAudioData(arrayBuffer);
      audioContext = context;
    } catch (e) {
      // Ignore audio load errors
    }
  })();

  return loadPromise;
}

/**
 * Warm the buffer up before it's needed. Call from a page that expects to play
 * the sound, so the first hover/click isn't the one that goes silent.
 */
export function preloadUISound(): void {
  void ensureLoaded();
}

/**
 * Play the tick. Silent (and harmless) until the buffer has loaded, and silent
 * if the browser has no Web Audio — callers never have to check.
 */
export function playUISound(): void {
  if (!audioContext || !audioBuffer) {
    void ensureLoaded();
    return;
  }

  try {
    // A context created before the first user gesture starts suspended; the
    // click that calls this IS that gesture, so resuming here is what makes the
    // very first press audible.
    if (audioContext.state === 'suspended') {
      void audioContext.resume();
    }

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;

    const filter = audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = LOWPASS_HZ;

    // SAFETY: gain is set before anything is connected, so the sound can never
    // reach the destination at full volume, even for a frame.
    const gainNode = audioContext.createGain();
    gainNode.gain.value = GAIN;

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);
    source.start(0);
  } catch (e) {
    // Ignore playback errors
  }
}

/* ------------------------------------------------------------------------- *
 * Game end: victory and defeat
 *
 * Both are built from the SAME sample as the tick above, pitched and stacked
 * into a short phrase. That is deliberate rather than lazy: the brief was "very
 * subtle, like the roll-over", and re-using the one sound the site already has
 * guarantees the ending is the same instrument as the hover rather than a
 * stranger borrowed from a sound pack. It also means no new asset, no licence.
 *
 * Victory rises (root, third, fifth), defeat falls (root, flat sixth below) —
 * played through the same low-pass so they stay soft thuds, not fanfares.
 *
 * ALL OF IT IS TUNABLE FROM THESE FOUR CONSTANTS. If it reads too loud, drop
 * the gain; too busy, cut a note; too quick, raise the spacing.
 * ------------------------------------------------------------------------- */

/** One pitched hit: `rate` is playback speed (pitch), `at` is seconds from now. */
interface Note {
  rate: number;
  at: number;
}

// Rising. 1.0 → 1.26 → 1.5 is roughly root / major third / fifth.
const VICTORY: Note[] = [
  { rate: 1.0, at: 0 },
  { rate: 1.26, at: 0.13 },
  { rate: 1.5, at: 0.26 },
];
const VICTORY_LOWPASS = 620;  // a touch brighter than the hover tick (400)
const VICTORY_GAIN = 0.05;

// Falling, and only two notes — an ending, not an event.
const DEFEAT: Note[] = [
  { rate: 1.0, at: 0 },
  { rate: 0.79, at: 0.18 },
];
const DEFEAT_LOWPASS = 280;   // duller than the hover tick
const DEFEAT_GAIN = 0.045;

// How long a note is allowed to ring before it's faded out. The sample is 0.7s
// with a long tail, and three overlapping tails turn to mud without this.
const NOTE_RELEASE = 0.55;

export function playVictorySound(): void {
  playPhrase(VICTORY, VICTORY_LOWPASS, VICTORY_GAIN);
}

export function playDefeatSound(): void {
  playPhrase(DEFEAT, DEFEAT_LOWPASS, DEFEAT_GAIN);
}

function playPhrase(notes: Note[], lowpassHz: number, peakGain: number): void {
  if (!audioContext || !audioBuffer) {
    void ensureLoaded();
    return;
  }

  try {
    const context = audioContext;
    if (context.state === 'suspended') {
      void context.resume();
    }

    const now = context.currentTime;
    for (const note of notes) {
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = note.rate;

      const filter = context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = lowpassHz;

      // SAFETY: gain is set before anything is connected (see playUISound).
      const gainNode = context.createGain();
      gainNode.gain.value = peakGain;
      // ...then ramped to silence, so the phrase ends instead of trailing off.
      gainNode.gain.setValueAtTime(peakGain, now + note.at);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + note.at + NOTE_RELEASE);

      source.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(context.destination);
      source.start(now + note.at);
      source.stop(now + note.at + NOTE_RELEASE);
    }
  } catch (e) {
    // Ignore playback errors
  }
}

/* ------------------------------------------------------------------------- *
 * A piece moved
 *
 * The sample the Sandbox plays when a piece is DROPPED ON THE BOARD — the same
 * one its three placement paths use (`/turn change.mp3` at 0.3), not the sandy
 * `/sand1.mp3` that plays when a piece is dragged back off into the tray.
 *
 * Quieter than the Sandbox's, because that one marks a deliberate placement
 * while this fires on EVERY move of every game, the opponent's included, so it
 * has to sit further back. One number to tune: MOVE_VOLUME.
 *
 * A plain HTMLAudioElement rather than the Web Audio path above, matching how
 * the rest of the app plays its samples, and ONE element reused: this can fire
 * several times a second during a fast exchange, and a new Audio per move leaks
 * elements. The trade-off is that a second move inside the sample restarts it
 * rather than layering, which is the behaviour you want for a move tick anyway.
 * ------------------------------------------------------------------------- */

const MOVE_SOUND_URL = '/turn change.mp3';
const MOVE_VOLUME = 0.12;

let moveAudio: HTMLAudioElement | null = null;

export function playMoveSound(): void {
  try {
    if (!moveAudio) {
      moveAudio = new Audio(MOVE_SOUND_URL);
      moveAudio.volume = MOVE_VOLUME;
    }
    moveAudio.currentTime = 0;
    void moveAudio.play().catch(() => {});
  } catch (e) {
    // Ignore playback errors
  }
}
